import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'
import { useSearchStore } from '@/stores/searchStore'
import { useUIStore } from '@/stores/uiStore'
import type { SearchPayload, MatchType } from '@/types'

// Query keys for cache management
export const queryKeys = {
  adminFiles: ['adminFiles'] as const,
  fileContent: (filename: string) => ['fileContent', filename] as const,
  searchOptions: ['searchOptions'] as const,
  health: ['health'] as const
}

/**
 * Hook to fetch admin/sample files list
 */
export function useAdminFiles() {
  return useQuery({
    queryKey: queryKeys.adminFiles,
    queryFn: () => api.getAdminFiles(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => data.files
  })
}

/**
 * Hook to fetch a specific file's content
 */
export function useFileContent(filename: string | null) {
  return useQuery({
    queryKey: queryKeys.fileContent(filename ?? ''),
    queryFn: () => api.getFileContent(filename!),
    enabled: !!filename,
    staleTime: 10 * 60 * 1000 // 10 minutes
  })
}

/**
 * Hook to check API health
 */
export function useHealthCheck() {
  const setApiConnected = useUIStore((state) => state.setApiConnected)

  return useQuery({
    queryKey: queryKeys.health,
    queryFn: async () => {
      const result = await api.checkHealth()
      setApiConnected(true)
      return result
    },
    retry: 3,
    refetchInterval: 30000, // Check every 30 seconds
    refetchOnWindowFocus: true
  })
}

/**
 * Hook to perform search
 */
export function useSearchMutation() {
  const {
    setSearchResults,
    setIsLoading,
    setError,
    setIsProgressiveMode
  } = useSearchStore()
  const addToast = useUIStore((state) => state.addToast)

  return useMutation({
    mutationFn: (payload: SearchPayload) => api.search(payload),
    onMutate: () => {
      setIsLoading(true)
      setError(null)
      setSearchResults(null)
      setIsProgressiveMode(false)
    },
    onSuccess: (data) => {
      setSearchResults(data)
    },
    onError: (error: Error) => {
      setError(error.message)
      addToast({
        type: 'error',
        title: 'Search Failed',
        message: error.message
      })
    },
    onSettled: () => {
      setIsLoading(false)
    }
  })
}

/**
 * Hook to perform progressive search
 */
export function useProgressiveSearch() {
  const {
    setIsLoading,
    setError,
    setIsProgressiveMode,
    updateProgressivePhase,
    setProgressiveResults,
    setSearchResults
  } = useSearchStore()
  const addToast = useUIStore((state) => state.addToast)

  // Progressive search now iterates through match types (excluding sentences for individual phases)
  const phases: MatchType[] = ['exact', 'prefix', 'suffix', 'contains']

  return useMutation({
    mutationFn: async (payload: { searchString: string; fileId: string }) => {
      setIsProgressiveMode(true)
      setProgressiveResults({})

      const results: Record<string, any> = {}

      for (const phase of phases) {
        try {
          const result = await api.progressiveSearch({
            searchString: payload.searchString,
            fileId: payload.fileId
          })
          updateProgressivePhase(phase, result)
          results[phase] = result

          // Small delay for better UX
          await new Promise((resolve) => setTimeout(resolve, 300))
        } catch (error) {
          console.error(`Progressive search error for ${phase}:`, error)
        }
      }

      return results
    },
    onMutate: () => {
      setIsLoading(true)
      setError(null)
    },
    onSuccess: (data, variables) => {
      // Combine all progressive results
      const combinedResults = {
        success: true,
        searchString: variables.searchString,
        results: {} as Record<string, any>,
        metadata: {
          searchMode: 'literal' as const,
          matchTypes: phases,
          timestamp: new Date().toISOString(),
          progressive: true
        }
      }

      for (const [, result] of Object.entries(data)) {
        if (result?.results) {
          Object.assign(combinedResults.results, result.results)
        }
      }

      setSearchResults(combinedResults as any)
    },
    onError: (error: Error) => {
      setError(error.message)
      addToast({
        type: 'error',
        title: 'Progressive Search Failed',
        message: error.message
      })
    },
    onSettled: () => {
      setIsLoading(false)
    }
  })
}

/**
 * Hook to load a file
 */
export function useLoadFile() {
  const queryClient = useQueryClient()
  const {
    setCurrentFileId,
    setCurrentFileInfo,
    setCurrentText,
    setError,
    setIsLoading,
    setSearchResults,
    setContentSource,
    setContentInfo
  } = useSearchStore()
  const addToast = useUIStore((state) => state.addToast)

  return useMutation({
    mutationFn: (filename: string) => api.getFileContent(filename),
    onMutate: () => {
      setIsLoading(true)
      setError(null)
    },
    onSuccess: (data, filename) => {
      setCurrentFileId(data.file.fileId)
      setCurrentFileInfo(data.file)
      setCurrentText(data.file.content || '')
      setContentSource('file')
      setContentInfo({
        source: 'file',
        name: data.file.filename,
        size: data.file.contentLength
      })
      setSearchResults(null)

      // Cache the file content
      queryClient.setQueryData(queryKeys.fileContent(filename), data)

      addToast({
        type: 'success',
        title: 'File Loaded',
        message: `${data.file.filename} is ready for search`
      })
    },
    onError: (error: Error, filename) => {
      setError(`Failed to load ${filename}: ${error.message}`)
      addToast({
        type: 'error',
        title: 'Failed to Load File',
        message: error.message
      })
    },
    onSettled: () => {
      setIsLoading(false)
    }
  })
}

/**
 * Hook to fetch content from URL (preview mode - doesn't auto-load)
 * Returns the fetched content for preview/selection
 */
export function useFetchUrlPreview() {
  const { setError } = useSearchStore()
  const addToast = useUIStore((state) => state.addToast)

  return useMutation({
    mutationFn: (url: string) => api.fetchUrl(url),
    onError: (error: Error) => {
      setError(`Failed to fetch URL: ${error.message}`)
      addToast({
        type: 'error',
        title: 'Failed to Fetch URL',
        message: error.message
      })
    }
  })
}

/**
 * Hook to upload a file
 */
export function useUploadFile() {
  const queryClient = useQueryClient()
  const {
    setCurrentFileId,
    setCurrentFileInfo,
    setCurrentText,
    setError,
    setIsLoading,
    setContentSource,
    setContentInfo
  } = useSearchStore()
  const addToast = useUIStore((state) => state.addToast)

  return useMutation({
    mutationFn: (file: File) => api.uploadFile(file),
    onMutate: () => {
      setIsLoading(true)
      setError(null)
    },
    onSuccess: (data) => {
      if (data.success && data.file) {
        setCurrentFileId(data.file.fileId)
        setCurrentFileInfo(data.file)
        setCurrentText(data.file.content || '')
        setContentSource('file')
        setContentInfo({
          source: 'file',
          name: data.file.filename,
          size: data.file.contentLength
        })

        // Invalidate admin files query to refresh the list
        queryClient.invalidateQueries({ queryKey: queryKeys.adminFiles })

        addToast({
          type: 'success',
          title: 'File Uploaded',
          message: `${data.file.filename} uploaded successfully`
        })
      }
    },
    onError: (error: Error) => {
      setError(`Upload failed: ${error.message}`)
      addToast({
        type: 'error',
        title: 'Upload Failed',
        message: error.message
      })
    },
    onSettled: () => {
      setIsLoading(false)
    }
  })
}
